/* Управление одним справочником: /manage/:kind. Только администраторам (ТЗ 3).
   Разделы разведены по строкам в «Ещё», поэтому здесь всегда один список
   и кнопка добавления — без вкладок. */

import {
  accountsOf, businessTotal, devStagesOf, goalColor, recurringMonthly, rub, salesStagesOf, shortNum,
} from "@mera/shared";
import { Glass, List, ListItem } from "konsta/react";
import React from "react";
import { useNavigate, useParams } from "../lib/router.jsx";

import { Avatar } from "../components/AvatarPicker.jsx";
import { Ion, IconTile } from "../components/icons.jsx";
import Screen from "../components/Screen.jsx";
import { SkelList } from "../components/skeleton.jsx";
import { BurnCards, Chips, Empty, ErrorState } from "../components/ui.jsx";
import { useApp, useFetch } from "../lib/store.jsx";
import { haptic } from "../lib/tg.js";

/* Подложки — те же цвета, что у строк раздела в «Ещё» */
export const MANAGE = {
  users: { title: "Люди", tile: "#8e8e93", icon: Ion.personSolid },
  businesses: { title: "Бизнесы", tile: "var(--tg-button)", icon: Ion.briefcaseSolid },
  goals: { title: "Цели", tile: "var(--fin-gold)", icon: Ion.flagSolid },
  recurring: { title: "Расходы", tile: "#ff9500", icon: Ion.repeatSolid },
};

const ITEM_COLORS = {
  primaryTextIos: "text-[color:var(--tg-text)]",
  secondaryTextIos: "text-[color:var(--tg-hint)]",
};

export default function Manage() {
  const { kind } = useParams();
  const { me } = useApp();
  const navigate = useNavigate();
  const spec = MANAGE[kind];

  if (!spec) return <Screen back><ErrorState text="Неизвестный раздел." /></Screen>;
  if (!me?.isAdmin) {
    return <Screen back><ErrorState text="Раздел доступен только администраторам." /></Screen>;
  }

  return (
    <Screen back title={spec.title} bottomPad={110}>
      {kind === "users" ? <Users /> : null}
      {kind === "businesses" ? <Businesses /> : null}
      {kind === "goals" ? <Goals /> : null}
      {kind === "recurring" ? <Recurring /> : null}

      <AddButton onClick={() => { haptic.medium(); navigate(`/edit/${kind}/new`); }} />
    </Screen>
  );
}

/* Кнопка добавления — стекло Konsta, по центру над нижним краем */
function AddButton({ onClick }) {
  return (
    <Glass
      component="button"
      className="fixed left-1/2 -translate-x-1/2 w-14 h-14 rounded-full flex items-center justify-center active:opacity-70 z-20"
      style={{ bottom: "calc(var(--tg-bottom) + 20px)", color: "var(--tg-button)" }}
      onClick={onClick}
      aria-label="Добавить"
    >
      <Ion.add size={28} />
    </Glass>
  );
}

/* Есть фото — показываем его кружком, нет — привычная плитка с иконкой */
const Tile = ({ item, kind }) =>
  item.avatar
    ? <Avatar src={item.avatar} name={item.name} size={29} />
    : <IconTile icon={MANAGE[kind].icon} bg={MANAGE[kind].tile} />;

const Row = ({ kind, id, ...rest }) => {
  const navigate = useNavigate();
  return (
    <ListItem
      link
      colors={ITEM_COLORS}
      onClick={() => { haptic.light(); navigate(`/edit/${kind}/${id}`); }}
      {...rest}
    />
  );
};

/* ── Участники ─────────────────────────────────────────── */

function Users() {
  const { data, loading, error, refetch } = useFetch("/admin/users");

  if (loading) return <SkelList />;
  if (error) return <ErrorState text={error} onRetry={refetch} />;
  if (!data.length) return <Empty text="Участников нет" icon={Ion.person} />;

  return (
    <List strong inset>
      {data.map((u) => (
        <Row
          key={u.id}
          kind="users"
          id={u.id}
          className={u.isActive ? "" : "opacity-50"}
          media={
            <IconTile
              icon={u.isAdmin || u.fromEnv ? Ion.shieldCheckmarkSolid : Ion.personSolid}
              bg={u.isAdmin || u.fromEnv ? "var(--fin-gold)" : MANAGE.users.tile}
            />
          }
          title={u.name}
          after={u.telegramId ? (u.isAdmin || u.fromEnv ? "админ" : null) : "нет доступа"}
        />
      ))}
    </List>
  );
}

/* ── Бизнесы ───────────────────────────────────────────── */

/* Справа у бизнеса — его деньги: счета заведены внутри него, и общая сумма
   по ним говорит больше, чем число этапов. Счетов нет — показываем, что заведено */
const bizSummary = (b) => {
  if (accountsOf(b).length) return <span className="num">{rub(businessTotal(b))}</span>;
  if (salesStagesOf(b).length) return `${salesStagesOf(b).length} этапов`;
  return devStagesOf(b).length ? "разработка" : "нет счетов";
};

function Businesses() {
  const { meta } = useApp();
  const list = meta?.businesses || [];

  if (!list.length) return <Empty text="Бизнесов нет" icon={Ion.briefcase} />;

  return (
    <List strong inset>
      {list.map((b) => (
        <Row
          key={b.id}
          kind="businesses"
          id={b.id}
          media={<Tile item={b} kind="businesses" />}
          title={b.name}
          after={bizSummary(b)}
        />
      ))}
    </List>
  );
}

/* ── Цели ──────────────────────────────────────────────── */

/* Значок ступени: закрытая — галочка, текущая — флажок, ждущая — замок */
const GOAL_ICON = {
  done: Ion.checkmarkCircleSolid,
  active: Ion.flagSolid,
  locked: Ion.lockClosedSolid,
};

/* Список идёт в том же порядке, что и лестница в обзоре, поэтому прогресс
   берём с сервера: деньги на счетах и доход в месяц он считает целиком. */
function Goals() {
  const { data, loading, error, refetch } = useFetch("/goals");

  if (loading) return <SkelList />;
  if (error) return <ErrorState text={error} onRetry={refetch} />;

  const list = data.goals || [];
  if (!list.length) return <Empty text="Целей нет" icon={Ion.flag} />;

  return (
    <>
      <List strong inset>
        {list.map((g) => (
          <Row
            key={g.id}
            kind="goals"
            id={g.id}
            media={
              <IconTile
                icon={GOAL_ICON[g.state]}
                /* Цвет цели горит только у того, что уже идёт — ждущая ступень серая */
                bg={g.state === "locked" ? "#8e8e93" : goalColor(g)}
              />
            }
            title={g.name}
            after={
              <span className="num">
                {shortNum(g.goal)}{g.kind === "income" ? "/мес" : ""} · {g.pct}%
              </span>
            }
          />
        ))}
      </List>
      <div className="px-8 -mt-6 text-[13px]" style={{ color: "var(--tg-hint)" }}>
        Цели идут по порядку: пока верхняя не закрыта, следующая ждёт своей очереди.
        Место в лестнице задаётся в самой цели.
      </div>
    </>
  );
}

/* ── Регулярные расходы ────────────────────────────────── */

function Recurring() {
  const { meta } = useApp();
  const list = meta?.recurring || [];
  const [biz, setBiz] = React.useState("all");

  // Фильтруем по бизнесам со счетами: расход делится именно между счетами
  const withMoney = (meta?.businesses || []).filter((b) => accountsOf(b).length);
  const picked = withMoney.find((b) => b.id === biz);

  if (!list.length) return <Empty text="Регулярных расходов нет" icon={Ion.repeat} />;

  /* Один платёж делится между счетами разных бизнесов, поэтому фильтр не выбрасывает
     запись целиком, а оставляет от неё долю выбранного бизнеса — и сумма в строке
     показывает, сколько платит именно он. Долей нет — записи в списке нет. */
  const accIds = new Set(accountsOf(picked).map((a) => a.id));
  const shown = picked
    ? list
      .map((r) => {
        const splits = (r.splits || []).filter((s) => accIds.has(s.acc));
        return { ...r, splits, amount: splits.reduce((s, x) => s + x.amount, 0) };
      })
      .filter((r) => r.amount)
    : list;

  const burn = shown.reduce((s, r) => s + recurringMonthly(r), 0);
  // Запас хода считаем по деньгам того же среза, что и burn
  const balance = picked
    ? businessTotal(picked)
    : withMoney.reduce((s, b) => s + businessTotal(b), 0);

  return (
    <>
      {withMoney.length > 1 ? (
        <Chips
          items={[
            { id: "all", label: "Все" },
            ...withMoney.map((b) => ({ id: b.id, label: b.name, avatar: b.avatar })),
          ]}
          value={biz}
          onChange={(v) => { haptic.select(); setBiz(v); }}
        />
      ) : null}

      <BurnCards total={balance} burn={burn} />

      {!shown.length ? (
        <Empty text="У этого бизнеса регулярных расходов нет" icon={Ion.repeat} />
      ) : (
        <List strong inset>
          {shown.map((r) => (
            <Row
              key={r.id}
              kind="recurring"
              id={r.id}
              media={<IconTile icon={Ion.repeatSolid} bg={MANAGE.recurring.tile} />}
              title={r.name}
              after={
                <span className="num">
                  {rub(recurringMonthly(r))}
                  {r.days?.length ? ` · ${r.days.join(", ")} ч.` : ""}
                </span>
              }
            />
          ))}
        </List>
      )}

      {shown.length ? (
        <div className="px-8 -mt-6 text-[13px]" style={{ color: "var(--tg-hint)" }}>
          {picked
            ? <>Доля «{picked.name}» в регулярных расходах — <span className="num">{rub(burn)}</span> в месяц.</>
            : <>Всего в месяц — <span className="num">{rub(burn)}</span>. Это burn, из него считается запас хода по счетам.</>}
        </div>
      ) : null}
    </>
  );
}
